<template>
  <main>
    <!-- Hero -->
    <section class="relative overflow-hidden px-4 pb-24 pt-32 text-center">
      <div
        class="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,_rgba(139,92,246,0.08)_0%,_transparent_70%)] dark:bg-[radial-gradient(ellipse_at_center,_rgba(139,92,246,0.15)_0%,_transparent_70%)]"
      />

      <h1 class="mx-auto max-w-3xl text-5xl font-extrabold leading-tight tracking-tight text-zinc-900 dark:text-zinc-50 sm:text-6xl">
        Create stories with the
        <span class="bg-gradient-to-r from-violet-600 to-indigo-500 bg-clip-text text-transparent dark:from-violet-500 dark:to-indigo-400">
          power of AI
        </span>
      </h1>

      <p class="mx-auto mt-6 max-w-xl text-lg text-zinc-500 dark:text-zinc-400">
        Pick a genre, choose your audience, and let YouStory craft a
        unique tale in seconds — powered by Google Gemini.
      </p>

      <div class="mt-10 flex items-center justify-center gap-4">
        <button
          v-if="!auth.isLoggedIn"
          @click="handleSignIn"
          class="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-500 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:shadow-violet-500/40"
        >
          <svg class="h-5 w-5" viewBox="0 0 48 48">
            <path fill="#fff" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" opacity=".6"/>
            <path fill="#fff" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" opacity=".8"/>
            <path fill="#fff" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" opacity=".6"/>
            <path fill="#fff" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" opacity=".8"/>
          </svg>
          Get Started with Google
        </button>

        <router-link
          v-else
          to="/create"
          class="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-500 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:shadow-violet-500/40"
        >
          Start Creating
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
          </svg>
        </router-link>
      </div>
    </section>

    <!-- Features Strip -->
    <section class="mx-auto max-w-6xl px-4 pb-32">
      <div class="grid gap-6 sm:grid-cols-3">
        <div
          v-for="feature in features"
          :key="feature.title"
          class="group rounded-2xl border border-zinc-200 bg-surface p-8 transition hover:border-violet-500/30 dark:border-white/5 dark:bg-surface-dark dark:hover:border-violet-500/20"
        >
          <div
            class="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-400"
          >
            <component :is="feature.icon" class="h-6 w-6" />
          </div>
          <h3 class="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{{ feature.title }}</h3>
          <p class="mt-2 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
            {{ feature.description }}
          </p>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { h } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';

const auth = useAuthStore();
const router = useRouter();

async function handleSignIn() {
  try {
    await auth.signIn();
    router.push('/create');
  } catch (e) {
    console.error('Sign-in failed:', e);
  }
}

const SparklesIcon = {
  render: () =>
    h(
      'svg',
      { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', 'stroke-width': '1.5' },
      [
        h('path', {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          d: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z',
        }),
      ]
    ),
};

const BookIcon = {
  render: () =>
    h(
      'svg',
      { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', 'stroke-width': '1.5' },
      [
        h('path', {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          d: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25',
        }),
      ]
    ),
};

const SpeakerIcon = {
  render: () =>
    h(
      'svg',
      { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', 'stroke-width': '1.5' },
      [
        h('path', {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          d: 'M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z',
        }),
      ]
    ),
};

const features = [
  {
    icon: SparklesIcon,
    title: 'AI-Powered Generation',
    description:
      'Google Gemini crafts unique stories based on your genre, audience, and language preferences.',
  },
  {
    icon: BookIcon,
    title: 'Your Personal Library',
    description:
      'Save every story to your cloud library. Browse, revisit, and replay your creations anytime.',
  },
  {
    icon: SpeakerIcon,
    title: 'Text-to-Speech',
    description:
      'Listen to your stories read aloud with built-in speech synthesis — perfect for bedtime tales.',
  },
];
</script>
